import { DateTime, Info } from 'luxon';
import { DateRange } from '../../../components/dates/RangeCalendar';
import { getLocale } from '../../../utils/i18n';
import { WorkoutWithCycleAndSession } from '../types';
import { ActualWorkout } from './types';

export type MonthView = {
  prescriptionWorkoutsInRange: WorkoutWithCycleAndSession[];
  actualsWorkoutsInRange: ActualWorkout[];
  monthName: string;
  monthRange: DateRange;
};

export function getMonthViews(
  dateRange: DateRange,
  prescriptionWorkouts: WorkoutWithCycleAndSession[],
  actualsWorkouts: ActualWorkout[],
): MonthView[] {
  const months: MonthView[] = [];
  const { start: rangeStart, end: rangeEnd } = dateRange;
  let cursor = DateTime.fromISO(rangeStart).startOf('day');
  const rangeEndDt = DateTime.fromISO(rangeEnd).startOf('day');

  while (cursor <= rangeEndDt) {
    const monthNum = cursor.month;
    const monthName = Info.months('long', { locale: getLocale() })[
      monthNum - 1
    ];
    // Clamp to the user's selected range end so workouts on days after
    // dateRange.end are excluded and render as disabled slots rather than
    // as active, interactive cells.
    const segmentEnd = DateTime.min(cursor.endOf('month'), rangeEndDt);
    const start = cursor.toISODate() ?? '';
    const end = segmentEnd.toISODate() ?? '';

    if (!monthName || !start || !end) break;

    const monthRange = { start, end };

    months.push({
      prescriptionWorkoutsInRange: prescriptionWorkouts.filter(
        ({ assignedDate }) => isWorkoutInDateRange(assignedDate, monthRange),
      ),
      actualsWorkoutsInRange: actualsWorkouts.filter(({ assignedDate }) =>
        isWorkoutInDateRange(assignedDate, monthRange),
      ),
      monthName,
      monthRange,
    });

    cursor = cursor.endOf('month').plus({ days: 1 }).startOf('day');
  }
  return months;
}

function isWorkoutInDateRange(
  assignedDate: string | null,
  monthRange: DateRange,
): boolean {
  if (!assignedDate) return false;
  const workoutDate = DateTime.fromISO(assignedDate).startOf('day');
  const start = DateTime.fromISO(monthRange.start).startOf('day');
  const end = DateTime.fromISO(monthRange.end).endOf('day');
  return workoutDate.isValid && workoutDate >= start && workoutDate <= end;
}
